import React, { useState, useEffect, useContext, useRef } from "react";
import _merge from "lodash/merge";
import { HeritageAndEthnicityList, LocationList } from "component-library";
import { WarningIcon } from "assets";
import { useTranslation } from "i18n";
import { UserClaimTypes, UserManager } from "app/core";
import {
  withEditBasicInfoContext,
  IEditBasicInfoContextProps,
} from "shared/contexts/EditBasicInfoContext";
import { FacetsViewModel, BasicInfoViewModel } from "../../../models/views";
import { pushToDataLayer, TRACKING_EVENT_TYPE } from "app/helpers/tracking";
import { ControlSection } from "shared/modules/Common";
import {
  ILocationContract,
  IGoogleLocationContract,
} from "../../../models/contracts";
import VisasAndPermits from "shared/modules/BasicInfo/components/EditBasicInfo/BasicInfoForm/sections/VisasAndPermits";
import {
  PlayingAge,
  Memberships,
  Height,
  Nationality,
  Disabilities,
  Gender,
  Name,
} from "./sections";
import { IBasicInfoFormContent } from "./BasicInfoFormContent";
import "./BasicInfoForm.scss";
import { FEATURE_FLAGS, useLaunchDarklyFlags } from "shared-services";
import {
  GlobalContextEventTypes,
  GlobalRouteContext,
} from "shared/contexts/GlobalContext";
import { PromiseContainer } from "shared/utils/promise";

export const heritageAndEthnicityFallback = {
  heritageAndEthnicityTitle: "Heritage & Ethnicity",
  heritageAndEthnicityHeader:
    "To indicate your ethnicity, you may select a broader ethnic background or add specific ethnicities in the fields below. Alternatively, you can choose a combination of both options.",
  heritageAndEthnicityWarningDescription:
    "Are you sure you want to add more than 3 ethnicities? Please ensure that you are only adding the most relevant information.",
  ethnicBackgroundLabel: "Add ethnic background:",
  ethnicBackgroundPlaceholder: "Please select",
  ethnicBackgroundTooltipContent: "Add ethnic background tooltip content",
  specificEthnicitiesLabel: "Add specific ethnicities:",
  specificEthnicitiesPlaceholder: "Please start typing...",
  specificEthnicitiesTooltipContent: "Add specific ethnicities tooltip content",
  heritageAndEthnicityFurtherInformationLabel:
    "Further information (optional):",
  heritageAndEthnicityFurtherInformationTooltipContent:
    "Further information tooltip content",
  heritageAndEthnicityVisibilityLabel:
    "Do you want your ethnicity displayed on your profile?",
  heritageAndEthnicityVisibilityTooltipContent:
    "Do you want your ethnicity displayed on your profile content",
  heritageAndEthnicityVisibilityYesInfo:
    "By selecting 'Yes', this information will be visible on your profile at all times.  Everyone who has a link to your profile will be able to see this information, it will be public.  To restrict visibility choose 'No'.",
  heritageAndEthnicityVisibilityNoInfo:
    "By selecting 'No', this information will only be visible if a casting professional filters their Performer search results by ethnicity.",
  heritageAndEthnicityClose: "Close",
  heritageAndEthnicityContinue: "Continue to add",
  heritageAndEthnicityYes: "Yes",
  heritageAndEthnicityNo: "No",
};

export interface IBasicInfoFormProps extends IEditBasicInfoContextProps {
  model: BasicInfoViewModel;
  facets: FacetsViewModel;
  applyChanges: (model: BasicInfoViewModel) => void;
  content: IBasicInfoFormContent;
  searchForLocation: (value: string) => Promise<ILocationContract[]>;
  roundLocation: (placeId: string) => Promise<IGoogleLocationContract>;
}

export const BasicInfoForm: React.FC<
  React.PropsWithChildren<IBasicInfoFormProps>
> = (props) => {
  const { t } = useTranslation();
  const isVisasAndPermitsOn = useLaunchDarklyFlags(
    FEATURE_FLAGS.VisasAndPermits
  );
  const [isShownContent, setIsShownContent] = useState(false);
  const globalContext = useContext(GlobalRouteContext);
  const approvePromise = useRef(new PromiseContainer<undefined>());

  const {
    searchForLocation,
    roundLocation,
    content,
    model,
    facets: {
      memberships,
      nationalities,
      disabilities,
      genders,
      heritageAndEthnicity,
      ethnicBackground,
      visasAndPermits,
    },
    sharedState,
  } = props;

  useEffect(() => {
    setIsShownContent(true);
  }, []);

  const applyChanges = (model: BasicInfoViewModel) => {
    props.applyChanges(model);
  };

  const handleEthnicityAndHeritageTracking = (values: any[]) => {
    pushToDataLayer(TRACKING_EVENT_TYPE.ChangeEthAndHer, {
      values,
      memberType: UserManager.getClaim(UserClaimTypes.role),
    });
  };

  const showAlertPopUp = async () => {
    const translations = _merge(heritageAndEthnicityFallback, content);

    globalContext.notifyListener(GlobalContextEventTypes.notifyingGlobalAlert, {
      popupProps: {
        width: { lg: 4, md: 6 },
        close: () => {
          approvePromise.current.reject();
        },
        priority: "high",
        texts: { closePopup: translations.heritageAndEthnicityClose },
      },
      alertMessageProps: {
        icon: <WarningIcon />,
        texts: {
          title: translations.heritageAndEthnicityTitle,
          description: translations.heritageAndEthnicityWarningDescription,
        },
        buttons: [
          {
            name: translations.heritageAndEthnicityContinue,
            click: () => approvePromise.current.resolve(),
            type: "primary",
          },
        ],
      },
      closeButton: {
        name: translations.heritageAndEthnicityClose,
        type: "secondary",
        click: () => {
          approvePromise.current.reject();
        },
      },
    });

    approvePromise.current.setPromise();

    await approvePromise.current.promise;

    globalContext.notifyListener(GlobalContextEventTypes.closeAllGlobalAlert);
  };

  if (!isShownContent) return null;

  return (
    <div className="c-basic-info-form">
      <PlayingAge
        content={content}
        model={model}
        handleChanges={applyChanges}
      />
      <ControlSection
        name={content.locationSectionName}
        description={content.locationSectionDescription}
      >
        <div className="c-basic-info-form__location">
          <LocationList
            searchForLocation={searchForLocation}
            roundLocation={roundLocation}
            content={content}
            model={model}
            handleChanges={applyChanges}
          />
        </div>
      </ControlSection>
      {sharedState.isVisibleSection("membership") && (
        <Memberships
          content={content}
          model={model}
          facet={memberships}
          handleChanges={applyChanges}
        />
      )}
      {isVisasAndPermitsOn && (
        <VisasAndPermits
          visasAndPermitsFacets={visasAndPermits}
          handleChange={applyChanges}
          model={model}
        />
      )}
      <Nationality
        content={content}
        model={model}
        facet={nationalities}
        handleChanges={applyChanges}
      />
      <ControlSection name={t("common:heritage.ethnicity.label")}>
        <p className="mb-[10px]">{content.heritageAndEthnicityHeader}</p>
        <HeritageAndEthnicityList
          heritageAndEthnicityFacet={heritageAndEthnicity}
          ethnicBackgroundFacet={ethnicBackground}
          handleChange={applyChanges}
          model={model}
          content={content}
          handleTrack={handleEthnicityAndHeritageTracking}
          showAlertPopUp={showAlertPopUp}
        />
      </ControlSection>
      {sharedState.isVisibleSection("gender") && (
        <Gender
          content={content}
          model={model}
          facet={genders}
          handleChanges={applyChanges}
        />
      )}
      <Name content={content} />
      <Height content={content} model={model} handleChanges={applyChanges} />
      {sharedState.isVisibleSection("disabilities") && (
        <Disabilities
          content={content}
          model={model}
          facet={disabilities}
          handleChanges={applyChanges}
        />
      )}
    </div>
  );
};

export default withEditBasicInfoContext(BasicInfoForm);
